// Browser-side persistence for the offline mirror. One database, object
// stores keyed by userId, so multiple accounts on a device never mix and a
// purge is a single deleteDatabase.
import type { OfflineSnapshot } from "./snapshot";

export const OFFLINE_DB = "rakemori-offline";
export const IMAGE_CACHE = "rakemori-offline-images";
export const SHELL_CACHE = "rakemori-offline-shell";

const SNAPSHOTS = "snapshots";
const META = "meta";

export type OfflineMeta = { userId: string; lastSyncedAt: string; quotaExceeded: boolean };

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(OFFLINE_DB, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(SNAPSHOTS)) db.createObjectStore(SNAPSHOTS);
      if (!db.objectStoreNames.contains(META)) db.createObjectStore(META, { keyPath: "userId" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(
  storeName: string,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(storeName, mode);
        const req = fn(t.objectStore(storeName));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        t.oncomplete = () => db.close();
        t.onabort = () => db.close();
      })
  );
}

export async function saveSnapshot(userId: string, snapshot: OfflineSnapshot): Promise<void> {
  await tx(SNAPSHOTS, "readwrite", (s) => s.put(snapshot, userId));
}

export async function loadSnapshot(userId: string): Promise<OfflineSnapshot | null> {
  const got = await tx<OfflineSnapshot | undefined>(SNAPSHOTS, "readonly", (s) => s.get(userId));
  return got ?? null;
}

export async function setMeta(meta: OfflineMeta): Promise<void> {
  await tx(META, "readwrite", (s) => s.put(meta));
}

export async function getMeta(userId: string): Promise<OfflineMeta | null> {
  const got = await tx<OfflineMeta | undefined>(META, "readonly", (s) => s.get(userId));
  return got ?? null;
}

/** The /offline page has no session to know "who": pick the most recently
    synced user's copy (in practice a device has one). */
export async function loadAnySnapshot(): Promise<{
  snapshot: OfflineSnapshot;
  meta: OfflineMeta | null;
} | null> {
  const metas = await tx<OfflineMeta[]>(META, "readonly", (s) => s.getAll());
  const keys = await tx<IDBValidKey[]>(SNAPSHOTS, "readonly", (s) => s.getAllKeys());
  if (keys.length === 0) return null;
  const byRecency = [...metas].sort((a, b) => b.lastSyncedAt.localeCompare(a.lastSyncedAt));
  const userId = (byRecency[0]?.userId ?? keys[0]) as string;
  const snapshot = await loadSnapshot(userId);
  if (!snapshot) return null;
  return { snapshot, meta: byRecency[0] ?? null };
}

/** Sign-out hygiene: drop the mirror and both caches. Best-effort — a failed
    purge must never block the sign-out itself. */
export async function purgeOfflineData(): Promise<void> {
  try {
    await new Promise<void>((resolve) => {
      const req = indexedDB.deleteDatabase(OFFLINE_DB);
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
    });
    if (typeof caches !== "undefined") {
      await caches.delete(IMAGE_CACHE);
      await caches.delete(SHELL_CACHE);
    }
  } catch {
    // Swallow: purging is hygiene, not a gate.
  }
}
